import Link from 'next/link';
// DIAGNOSTIC: Testing direct import from lucide-react instead of /lib/icons
import { ArrowRight, Calendar, User, Clock } from '@/lib/icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface BlogCardProps {
  title: string;
  description: string;
  slug: string;
  category: string;
  author: string;
  publishDate: string;
  readTime: string;
  featured?: boolean;
  heroImage?: string | null;
}

export function BlogCard({
  title,
  description,
  slug,
  category,
  author,
  publishDate,
  readTime,
  featured = false,
  heroImage,
}: BlogCardProps) {
  return (
    <Link href={`/blog/${slug}`}>
      <Card className={`group hover:shadow-lg transition-all duration-300 h-full overflow-hidden ${featured ? 'border-primary/20 bg-gradient-to-br from-primary/5 to-transparent' : ''}`}>

        {/* Hero image or branded placeholder — same fixed height either way */}
        {heroImage ? (
          <div className="w-full h-48 overflow-hidden">
            <img
              src={heroImage}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : (
          <div
            className="w-full h-48 bg-gradient-to-br from-gray-50 via-primary/5 to-primary/10 flex items-center justify-center"
            aria-hidden="true"
          >
            <svg className="w-10 h-10 text-primary/20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z" />
            </svg>
          </div>
        )}

        <CardHeader>
          <div className="flex items-center justify-between mb-3">
            <Badge variant={featured ? "default" : "secondary"} className="text-xs">
              {category}
            </Badge>
            {featured && (
              <Badge variant="outline" className="text-xs border-primary text-primary">
                Featured
              </Badge>
            )}
          </div>
          <CardTitle className="text-xl group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </CardTitle>
          <CardDescription className="line-clamp-3 text-base">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span>{author}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{publishDate}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{readTime}</span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-primary" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}